import { useState } from 'react';
import { Link } from 'react-router-dom';
import { L } from './tokens';
import { ResultIcon } from './searchIcons';
import { caseAmount, caseSub, caseTitle, companySub, documentSub, documentTitle, hitTarget, matchesLine } from './searchFormat';
import { openOriginalFile } from './openOriginal';

// Leo's "Search results" board: the title with the term, the match count, and three cards: Cases, Documents, Companies.

const rowBase = (hover) => ({
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 16px', padding: '12px 14px', borderRadius: 12, textDecoration: 'none', color: L.text,
  background: hover ? L.chipBg : 'transparent', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit', boxSizing: 'border-box',
});

function Row({ icon, title, sub, amount, target, onOpenFile }) {
  const [hover, setHover] = useState(false);
  const body = (
    <>
      <ResultIcon kind={icon} />
      {/* The text takes the room it needs (at least 150 px) and wraps at word boundaries; the amount moves to its own line, right-aligned, when the card is narrow. */}
      <div style={{ flex: '1 1 150px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="row-text">
        <span style={{ fontSize: 16, fontWeight: 600, overflowWrap: 'break-word' }}>{title}</span>
        {sub ? <span style={{ fontSize: 14, color: L.muted, overflowWrap: 'break-word' }}>{sub}</span> : null}
      </div>
      {amount ? <span style={{ flex: '0 0 auto', marginLeft: 'auto', fontFamily: L.serif, fontSize: 26, fontWeight: 600, whiteSpace: 'nowrap', color: L.accent }} data-testid="row-amount">{amount}</span> : null}
    </>
  );
  const hoverProps = { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) };
  if (target.to) return <Link to={target.to} style={rowBase(hover)} data-testid="result-row" {...hoverProps}>{body}</Link>;
  if (target.file) return <button type="button" onClick={() => onOpenFile(target.file)} style={{ ...rowBase(hover), cursor: 'pointer' }} data-testid="result-row" {...hoverProps}>{body}</button>;
  return <div style={rowBase(false)} data-testid="result-row">{body}</div>;      // a customer: no page yet
}

function Card({ title, group, empty, showing, children }) {
  return (
    <section aria-label={title} style={{ background: L.card, border: `1px solid ${L.border}`, borderRadius: 20, padding: '24px 28px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0, fontFamily: L.serif, fontSize: 23, fontWeight: 500 }}>{title}</h2>
        {group.total > group.rows.length ? <span style={{ fontSize: 13, color: L.muted }} data-testid="group-more">{showing}</span> : null}
      </div>
      {group.rows.length === 0 ? <div style={{ fontSize: 14, color: L.muted }}>{empty}</div> : children}
    </section>
  );
}

export default function SearchResultsView({ q, data, t, lang, kind }) {
  const [fileError, setFileError] = useState(false);
  const openFile = async (id) => { setFileError(false); if (!(await openOriginalFile(id))) setFileError(true); };
  const { cases, documents, companies } = data.groups;
  const showing = (g) => t('search.showing', { shown: g.rows.length, total: g.total.toLocaleString('en-GB') + (g.capped ? '+' : '') });
  // Leo's three columns from 1280 px; below that as many columns as fit at 320 px each (never a card too narrow to read); one column on a phone.
  const columns = kind === 'wide' ? '1.2fr 1.2fr 1fr' : kind === 'phone' ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(320px, 1fr))';
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ margin: 0, fontFamily: L.serif, fontSize: kind === 'phone' ? 28 : 36, fontWeight: 500, overflowWrap: 'anywhere' }} data-testid="results-title">{t('search.title', { q })}</h1>
        <div style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: L.muted }} data-testid="results-count">{matchesLine(t, data)}</div>
      </div>
      {fileError ? <div role="alert" style={{ fontSize: 14, color: '#9E2A1C' }}>{t('search.fileError')}</div> : null}
      <div style={{ display: 'grid', gridTemplateColumns: columns, gap: 22 }}>
        <Card title={t('search.group.cases')} group={cases} empty={t('search.groupEmpty')} showing={showing(cases)}>
          {cases.rows.map((c) => (
            <Row key={c.id} icon={c.chip === 'matched' ? 'done' : (c.chip || 'open')} title={caseTitle(t, c)} sub={caseSub(t, c)} amount={caseAmount(c)} target={hitTarget(c)} onOpenFile={openFile} />
          ))}
        </Card>
        <Card title={t('search.group.documents')} group={documents} empty={t('search.groupEmpty')} showing={showing(documents)}>
          {documents.rows.map((d) => (
            <Row key={d.id} icon={d.type} title={documentTitle(t, d)} sub={documentSub(t, lang, d)} target={hitTarget(d)} onOpenFile={openFile} />
          ))}
        </Card>
        <Card title={t('search.group.companies')} group={companies} empty={t('search.groupEmpty')} showing={showing(companies)}>
          {companies.rows.map((c) => (
            <Row key={`${c.role}:${c.id}`} icon="company" title={c.name} sub={companySub(t, c)} target={hitTarget(c)} onOpenFile={openFile} />
          ))}
        </Card>
      </div>
    </>
  );
}
